/* Officer words for record values that a sentence needs in a grammatical
 * form (SENTENCES, NOT LABELS — the drawer and the evidence rows,
 * 2026-09-03). Every function is deterministic: the same record value
 * always gives the same words, and an unknown value falls back to the
 * value itself in plain form — never to a placeholder.
 */

import { humanizeToken } from '../officerFormat'

/** The article before a legal form or a noun — "an AG", "a GmbH",
 *  "an SCSp", "a SICAV", "an LP", "a Ltd". Initialisms (two leading
 *  capitals, under four letters) go by the NAME of their first letter;
 *  words go by their first sound. */
export function articleFor(word: string | null | undefined): string {
  if (!word) return 'a'
  const w = word.trim()
  const initialism = /^[A-Z]{2}/.test(w) && !/^[A-Z]{4,}$/.test(w)
  if (initialism) return /^[AEFHILMNORSX]/.test(w) ? 'an' : 'a'
  return /^[AEIOUaeiou]/.test(w) ? 'an' : 'a'
}

/** "Switzerland" / "CH" → "Swiss" — the adjective a sentence needs for
 *  "under Swiss law". Unknown jurisdictions read "under the law of X". */
const LAW_ADJECTIVE: Record<string, string> = {
  switzerland: 'Swiss',
  ch: 'Swiss',
  luxembourg: 'Luxembourg',
  lu: 'Luxembourg',
  germany: 'German',
  de: 'German',
  austria: 'Austrian',
  at: 'Austrian',
  italy: 'Italian',
  it: 'Italian',
  france: 'French',
  fr: 'French',
  netherlands: 'Dutch',
  'the netherlands': 'Dutch',
  nl: 'Dutch',
  belgium: 'Belgian',
  be: 'Belgian',
  spain: 'Spanish',
  es: 'Spanish',
  ireland: 'Irish',
  ie: 'Irish',
  'united kingdom': 'English',
  uk: 'English',
  gb: 'English',
  'england and wales': 'English',
  liechtenstein: 'Liechtenstein',
  li: 'Liechtenstein',
  jersey: 'Jersey',
  je: 'Jersey',
  guernsey: 'Guernsey',
  gg: 'Guernsey',
  'cayman islands': 'Cayman Islands',
  ky: 'Cayman Islands',
  'united states': 'US',
  us: 'US',
  delaware: 'Delaware',
  denmark: 'Danish',
  dk: 'Danish',
  sweden: 'Swedish',
  se: 'Swedish',
  norway: 'Norwegian',
  no: 'Norwegian',
  finland: 'Finnish',
  fi: 'Finnish',
  portugal: 'Portuguese',
  pt: 'Portuguese',
  malta: 'Maltese',
  mt: 'Maltese',
  singapore: 'Singapore',
  sg: 'Singapore',
  'hong kong': 'Hong Kong',
  hk: 'Hong Kong',
}

/** The words that fill "under «law» law": "Swiss" — or, when no adjective
 *  is known, "the law of Ruritania" is what the caller should print, so
 *  this returns { adjective } or { of }. */
export function lawWords(jurisdiction: string | null | undefined): {
  adjective: string | null
  of: string | null
} {
  if (!jurisdiction || !jurisdiction.trim()) return { adjective: null, of: null }
  const key = jurisdiction.trim().toLowerCase()
  const adj = LAW_ADJECTIVE[key]
  return adj ? { adjective: adj, of: null } : { adjective: null, of: jurisdiction.trim() }
}

/** entity_nature → what the entity IS, in words. */
export function natureWords(nature: string | null | undefined): string | null {
  if (!nature) return null
  switch (nature) {
    case 'legal_person':
      return 'a legal person'
    case 'natural_person':
      return 'a natural person'
    case 'collective_vehicle':
      return 'a collective investment vehicle'
    case 'real_asset':
      return 'a real asset'
    default:
      return humanizeToken(nature)
  }
}

export type EvidenceTargetType = 'entity' | 'instrument' | 'position' | 'trade'

/** evidential_purpose → what the document SHOWS, for this kind of object:
 *  "the company's existence", "who holds it", "that the movement happened". */
export function purposeWords(
  purpose: string | null | undefined,
  target: EvidenceTargetType,
  nature?: string | null
): string {
  const p = (purpose ?? '').trim()
  const subject =
    target === 'entity'
      ? nature === 'collective_vehicle'
        ? "the fund's"
        : nature === 'natural_person'
          ? "the person's"
          : nature === 'real_asset'
            ? "the asset's"
            : "the company's"
      : target === 'instrument'
        ? "the instrument's"
        : target === 'position'
          ? "the position's"
          : "the movement's"
  switch (p) {
    case 'existence':
      return `${subject} existence`
    case 'ownership':
      return target === 'position' ? 'who holds it and how much' : 'who holds it'
    case 'transaction':
      return target === 'trade' ? 'that the movement happened' : 'a movement in it'
    case 'registry_recency':
      return `${subject} standing on the register`
    case 'balance':
      return `${subject} balance`
    case 'authorization':
    case 'authorisation':
      return 'that it was authorised'
    case 'settlement':
      return 'that the cash moved'
    case 'valuation':
      return `${subject} value`
    case '':
      return 'something not stated'
    default:
      return humanizeToken(p)
  }
}

/** evidential_weight → words. */
export function weightWords(weight: string | null | undefined): string {
  switch (weight) {
    case 'primary':
      return 'primary proof'
    case 'supporting':
      return 'supporting'
    case 'informational':
      return 'context only'
    default:
      return weight ? humanizeToken(weight) : 'weight not stated'
  }
}

/** validity_status → words. */
export function validityWords(status: string | null | undefined): string {
  switch (status) {
    case 'valid':
      return 'valid'
    case 'expired':
      return 'expired'
    case 'superseded':
      return 'superseded'
    case 'pending':
      return 'being checked'
    default:
      return status ? humanizeToken(status) : 'validity not assessed'
  }
}

/** Which validity-window clause a piece of evidence gets. */
export function windowMeaning(ev: { valid_from: string | null; valid_to: string | null }): string {
  if (ev.valid_from && ev.valid_to) return 'evidence.window.between'
  if (ev.valid_from) return 'evidence.window.from'
  if (ev.valid_to) return 'evidence.window.until'
  return 'evidence.window.none'
}

/** A record event type → what happened, in words: "entity.created" →
 *  "the entity was created"; unknown shapes fall back to plain words. */
const VERB_PAST: Record<string, string> = {
  created: 'was created',
  updated: 'was updated',
  deleted: 'was deleted',
  corrected: 'was corrected',
  confirmed: 'was confirmed',
  superseded: 'was superseded',
  registered: 'was registered',
  satisfied: 'was satisfied',
  expired: 'expired',
  mapped: 'was mapped to a demand',
  opened: 'was opened',
  resolved: 'was resolved',
}
export function eventWords(eventType: string | null | undefined): string | null {
  if (!eventType) return null
  const [subject, ...rest] = eventType.split(/[._]/).filter(Boolean)
  if (rest.length === 0) return humanizeToken(eventType)
  const verb = rest.join(' ')
  const past = VERB_PAST[verb]
  return past ? `the ${subject.replace(/_/g, ' ')} ${past}` : `${subject} ${verb}`
}

/** A record event's source type → "a confirmation", "an officer correction". */
export function sourceWords(sourceType: string | null | undefined): string | null {
  if (!sourceType) return null
  const words = humanizeToken(sourceType)
  return `${articleFor(words)} ${words}`
}
